import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  InputAdornment, LinearProgress, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Facilitator } from './facilitator';
import { ExamPaper, SavePaperPayload, examPaperService } from '../common/services/examPaperService';

const EMPTY_FORM = (): SavePaperPayload => ({
  title: '', description: null, examName: '', subject: null, durationMins: 60,
});

const StatusChip: React.FC<{ status: 0 | 1 }> = ({ status }) => (
  <Chip
    size="small"
    label={status === 1 ? 'Released' : 'Draft'}
    color={status === 1 ? 'success' : 'default'}
    variant={status === 1 ? 'filled' : 'outlined'}
    sx={{ fontWeight: 600, fontSize: 11 }}
  />
);

export const AdminExamPapers: React.FC = () => {
  const navigate = useNavigate();
  const [papers, setPapers] = useState<ExamPaper[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');

  // Add / Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ExamPaper | null>(null);
  const [form, setForm] = useState<SavePaperPayload>(EMPTY_FORM());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<ExamPaper | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setPapers(await examPaperService.getAll());
    } catch {
      setLoadError('Failed to load exam papers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = papers.filter(p => {
    const q = search.toLowerCase();
    return !q || p.title.toLowerCase().includes(q) || p.examName.toLowerCase().includes(q) || (p.subject ?? '').toLowerCase().includes(q);
  });

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_FORM()); setFormError(''); setDialogOpen(true); };
  const openEdit = (p: ExamPaper) => {
    setEditTarget(p);
    setForm({ title: p.title, description: p.description, examName: p.examName, subject: p.subject, durationMins: p.durationMins });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setFormError('Title is required.'); return; }
    if (!form.examName.trim()) { setFormError('Exam name is required.'); return; }
    if (!form.durationMins || form.durationMins < 1) { setFormError('Duration must be at least 1 minute.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editTarget) {
        const updated = await examPaperService.update(editTarget.examPaperId, form);
        setPapers(prev => prev.map(p => p.examPaperId === editTarget.examPaperId ? { ...p, ...updated } : p));
      } else {
        const created = await examPaperService.create(form);
        setPapers(prev => [created, ...prev]);
      }
      setDialogOpen(false);
    } catch {
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await examPaperService.delete(deleteTarget.examPaperId);
      setPapers(prev => prev.filter(p => p.examPaperId !== deleteTarget.examPaperId));
      setDeleteTarget(null);
    } catch {
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleRelease = async (p: ExamPaper) => {
    try {
      if (p.status === 1) {
        await examPaperService.retract(p.examPaperId);
        setPapers(prev => prev.map(x => x.examPaperId === p.examPaperId ? { ...x, status: 0, statusLabel: 'Draft' } : x));
      } else {
        await examPaperService.release(p.examPaperId);
        setPapers(prev => prev.map(x => x.examPaperId === p.examPaperId ? { ...x, status: 1, statusLabel: 'Released' } : x));
      }
    } catch { }
  };

  return (
    <Facilitator>
      <Box sx={{ p: 3 }}>

        {/* Header */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, p: 1, display: 'flex' }}>
              <AssignmentIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Exam Papers</Typography>
              <Typography variant="caption" color="text.secondary">
                {papers.length} paper{papers.length !== 1 ? 's' : ''} · build questions and release when ready
              </Typography>
            </Box>
          </Box>
          <TextField
            size="small"
            placeholder="Search title, exam or subject…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: search
                ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment>
                : null,
            }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            New Paper
          </Button>

        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>
        ) : loadError ? (
          <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{loadError}</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Exam</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                  <TableCell sx={{ width: 80, textAlign: 'center', fontWeight: 700 }}>Questions</TableCell>
                  <TableCell sx={{ width: 90, textAlign: 'center', fontWeight: 700 }}>Duration</TableCell>
                  <TableCell sx={{ width: 90, textAlign: 'center', fontWeight: 700 }}>Marks</TableCell>
                  <TableCell sx={{ width: 100, textAlign: 'center', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ width: 110, textAlign: 'center', fontWeight: 700 }}>Created</TableCell>
                  <TableCell sx={{ width: 160, textAlign: 'center', fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      {search ? `No papers match "${search}".` : 'No exam papers yet. Click "New Paper" to get started.'}
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map(p => (
                  <TableRow key={p.examPaperId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.title}</Typography>
                      {p.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240, display: 'block' }}>
                          {p.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="body2">{p.examName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{p.subject ?? '—'}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip label={p.questionCount} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{p.durationMins} min</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{p.totalMarks}</Typography>
                    </TableCell>
                    <TableCell align="center"><StatusChip status={p.status} /></TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">{moment(p.createdOn).format('D MMM YYYY')}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Build Questions">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/Facilitator/ExamPaper/${p.examPaperId}`)}>
                          <BuildIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Details">
                        <IconButton size="small" onClick={() => openEdit(p)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={p.status === 1 ? 'Retract' : 'Release'}>
                        <IconButton
                          size="small"
                          color={p.status === 1 ? 'warning' : 'success'}
                          onClick={() => handleToggleRelease(p)}
                        >
                          {p.status === 1 ? <UnpublishedIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add / Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography fontWeight={700}>{editTarget ? 'Edit Exam Paper' : 'New Exam Paper'}</Typography>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1.5 }}>
              <TextField
                label="Title *"
                fullWidth size="small"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. K-SAT Phase 1 – 2026"
              />
              <TextField
                label="Exam Name *"
                fullWidth size="small"
                value={form.examName}
                onChange={e => setForm(f => ({ ...f, examName: e.target.value }))}
                placeholder="e.g. K-SAT"
              />
              <TextField
                label="Subject"
                fullWidth size="small"
                value={form.subject ?? ''}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value || null }))}
                placeholder="e.g. Mathematics"
              />
              <TextField
                label="Duration (minutes) *"
                type="number" size="small" sx={{ width: 200 }}
                value={form.durationMins}
                onChange={e => setForm(f => ({ ...f, durationMins: Number(e.target.value) }))}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Description"
                fullWidth size="small" multiline minRows={2}
                value={form.description ?? ''}
                onChange={e => setForm(f => ({ ...f, description: e.target.value || null }))}
              />
              {formError && <Alert severity="error" sx={{ py: 0.5 }}>{formError}</Alert>}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : undefined}
            >
              {saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Create Paper'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirm */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>Delete Exam Paper?</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              All questions and options will be permanently deleted.
            </Typography>
            {deleteTarget && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" fontWeight={600}>"{deleteTarget.title}"</Typography>
              </Box>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button
              variant="contained" color="error" onClick={handleDelete} disabled={deleting}
              startIcon={deleting ? <CircularProgress size={14} /> : undefined}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Facilitator>
  );
};
